import { useCallback, useEffect, useMemo, useState } from 'react';
import { baixarCsv } from '../../design/csv';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { linhaClicavel } from '../../design/linha-clicavel';
import { fmt } from '../../design/graficos/modelos';
import { lerSemana } from '../../premiacao';
import type { ChequeDaSemana } from '../../premiacao';
import { BarraDoPrograma, Conta, filtroDoPrograma } from '../inovapay/Programa';
import { PROGRAMAS } from './conta-da-semana';
import { totalDaEquipe, tudoPorBalconista } from './tudo-junto';
import type { NumerosDoPrograma } from './tudo-junto';
import '../central/central.css';
import '../inovapay/inovapay.css';

/**
 * TUDO POR BALCONISTA (dono, 05/10/2026): "quanto cada balconista vendeu, devolvido, cada coisa —
 * vitamina, pré-vencido, paradão — e total". Uma linha por pessoa, com o filtro de período dos
 * programas. A conta vem pronta do cheque (/api/premiacao/semana, até 62 dias); ver tudo-junto.ts.
 */

const ROTA = 'tudo-por-balconista';
const n = (x: number) => fmt(x, 'inteiro');
const diaCurto = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

function Celulas({ x }: { x: NumerosDoPrograma }) {
  return (
    <>
      <td className={`cv-dir ${x.vendido ? 'cv-forte' : 'cv-cinza'}`}>{n(x.vendido)}</td>
      <td className={`cv-dir ${x.devolvido ? 'cv-neg' : 'cv-cinza'}`}>{n(x.devolvido)}</td>
    </>
  );
}

export function TudoPorBalconista({ parametros }: { parametros: URLSearchParams }) {
  const f = filtroDoPrograma(parametros);
  const periodo = f.periodo;
  const [ch, setCh] = useState<ChequeDaSemana | null>(null);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState('');

  const carregar = useCallback(() => {
    setErro(false);
    setCh(null);
    void lerSemana(periodo.de, periodo.ate).then((r) => (r.ok ? setCh(r.dados) : setErro(true)));
  }, [periodo.de, periodo.ate]);
  useEffect(carregar, [carregar]);

  const todas = useMemo(() => (ch ? tudoPorBalconista(ch) : []), [ch]);
  const linhas = busca.trim()
    ? todas.filter((l) => l.nome.toLowerCase().includes(busca.trim().toLowerCase()))
    : todas;
  const tot = useMemo(() => totalDaEquipe(todas), [todas]);

  const exportar = () => {
    if (!ch) return;
    baixarCsv(`tudo-por-balconista-${periodo.de}-a-${periodo.ate}.csv`, [
      [
        'Balconista',
        'Código',
        ...PROGRAMAS.flatMap((p) => [
          `${p.nome} vendido`,
          `${p.nome} devolvido`,
          `${p.nome} líquido`,
          `${p.nome} premiação`,
        ]),
        'Total vendido',
        'Total devolvido',
        'Total líquido',
        'A receber',
      ],
      ...todas.map((l) => [
        l.nome,
        l.codigo,
        ...PROGRAMAS.flatMap((p) => {
          const x = l.programas[p.id];
          return [x.vendido, x.devolvido, x.liquido, x.premiacao];
        }),
        l.total.vendido,
        l.total.devolvido,
        l.total.liquido,
        l.total.premiacao,
      ]),
    ]);
  };

  return (
    <section className="cv">
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>Tudo por balconista</h1>
            <div className="vg-contexto">
              De {diaCurto(periodo.de)} a {diaCurto(periodo.ate)} · vendido e devolvido de vitamina,
              pré-vencido e paradão, por pessoa — a mesma conta do cheque
            </div>
          </div>
          <div className="vg-acoes">
            <button
              type="button"
              className="vg-botao vg-botao-leve"
              onClick={exportar}
              disabled={!ch}
            >
              <Icone nome="down" tamanho={15} traco={1.6} />
              Exportar
            </button>
          </div>
        </header>
        <BarraDoPrograma f={f} rota={ROTA} maxDias={62} />

        {erro ? (
          <ErroDeArea oQue="as vendas por balconista" aoTentar={carregar} />
        ) : (
          <>
            <div className="ip-contas">
              {PROGRAMAS.map((p) => (
                <Conta
                  key={p.id}
                  rotulo={`${p.nome} · vendido · devolvido`}
                  valor={
                    ch
                      ? `${n(tot.programas[p.id].vendido)} · ${n(tot.programas[p.id].devolvido)}`
                      : null
                  }
                  tom="neutro"
                />
              ))}
              <Conta
                rotulo="Total líquido (vendido − devolvido)"
                valor={ch ? `${n(tot.total.liquido)} itens` : null}
                tom="ok"
              />
              <Conta
                rotulo="A receber da equipe"
                valor={ch ? fmt(tot.total.premiacao, 'reais') : null}
                tom="ok"
              />
            </div>

            <div className="vg-bloco cv-lista">
              <div className="vg-bloco-topo ip-tj-topo">
                <h2>Por balconista</h2>
                <label className="ip-tj-busca">
                  <Icone nome="search" tamanho={14} traco={1.6} />
                  <input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar pessoa…"
                    aria-label="Buscar pessoa"
                  />
                </label>
              </div>
              {!ch ? (
                <div className="vg-lista-osso" aria-busy="true">
                  {[0, 1, 2, 3].map((k) => (
                    <i key={k} />
                  ))}
                </div>
              ) : linhas.length === 0 ? (
                <p className="cv-vazio">
                  {busca.trim()
                    ? 'Ninguém com esse nome no período.'
                    : 'Ninguém vendeu vitamina, pré-vencido ou paradão neste período.'}
                </p>
              ) : (
                <div className="cv-rolagem">
                  <table className="cv-tabela ip-tj-tabela">
                    <thead>
                      <tr>
                        <th>Balconista</th>
                        {PROGRAMAS.map((p) => [
                          <th key={`${p.id}v`} className="cv-dir ip-tj-grupo">
                            {p.nome} vendido
                          </th>,
                          <th key={`${p.id}d`} className="cv-dir">
                            {p.nome} devolvido
                          </th>,
                        ])}
                        <th className="cv-dir ip-tj-grupo">Total vendido</th>
                        <th className="cv-dir">Total devolvido</th>
                        <th className="cv-dir">Total líquido</th>
                        <th className="cv-dir">A receber</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.map((l) => (
                        <tr
                          key={l.pessoaId}
                          className="cv-clicavel"
                          {...linhaClicavel(
                            () =>
                              (window.location.hash = `#/conferencia?pessoa=${l.pessoaId}&semana=${periodo.de}`),
                          )}
                        >
                          <td>
                            <div className="cv-forte">{l.nome}</div>
                            <div className="cv-cinza cv-peq">
                              {l.codigo ? `cód ${l.codigo}` : ''}
                            </div>
                          </td>
                          {PROGRAMAS.map((p) => (
                            <Celulas key={p.id} x={l.programas[p.id]} />
                          ))}
                          <Celulas x={l.total} />
                          <td className="cv-dir cv-forte">{n(l.total.liquido)}</td>
                          <td className="cv-dir cv-forte">{fmt(l.total.premiacao, 'reais')}</td>
                        </tr>
                      ))}
                    </tbody>
                    {!busca.trim() && (
                      <tfoot>
                        <tr className="ip-tj-total">
                          <td>Total da equipe</td>
                          {PROGRAMAS.map((p) => (
                            <Celulas key={p.id} x={tot.programas[p.id]} />
                          ))}
                          <Celulas x={tot.total} />
                          <td className="cv-dir">{n(tot.total.liquido)}</td>
                          <td className="cv-dir">{fmt(tot.total.premiacao, 'reais')}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
              <div className="cv-paginacao">
                <span>{ch ? `${n(linhas.length)} balconista(s)` : ''}</span>
                <span>
                  Vendido = lançou e o HOS mostra a venda · devolvido = devolução no HOS · clique na
                  pessoa para ver linha a linha
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
